import React from 'react';
import { Card, Row, Col, Container } from 'react-bootstrap';
import { useLocation, useParams } from 'react-router-dom';
import { GetPublic } from '@app/utils/httpProvider';
import { useQuery } from 'react-query';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import { Table } from 'react-bootstrap';
import 'react-tabs/style/react-tabs.css';
import {
    ASSET_API, BaseGatewayImage
} from '../../utils/apiUrl.js';
import { isValidUrl } from '../../utils/helper.js';
import HorizontalFieldsRow from '@app/components/common/HorizontalFieldsRow';
import moment from 'moment';
import 'react-datepicker/dist/react-datepicker.css';

const AssetPublicDetail = () => {
    const { id } = useParams();
    const location = useLocation();
    
    // Fallback to query param if not in URL segment
    const paramUrl = new URLSearchParams(location.search.substring(1));
    const itemId = id || paramUrl.get('id');

    const itemQuery = useQuery(['publicAsset', itemId], () =>
        GetPublic(ASSET_API.GET_PUBLIC_ITEM, itemId)
    );

    const isLoading = itemQuery.isLoading;
    const isError = itemQuery.isError;
    const error = itemQuery.error;
    const asset = itemQuery.data;

    const getAccessoryStatusStyle = (status) => {
        switch (status) {
            case 'Hoạt động': return { bg: 'rgba(40,167,69,0.12)', border: 'rgba(40,167,69,0.5)', text: '#28a745' };
            case 'Không hoạt động': return { bg: 'rgba(108,117,125,0.12)', border: 'rgba(108,117,125,0.5)', text: '#6c757d' };
            case 'Hỏng': return { bg: 'rgba(220,53,69,0.12)', border: 'rgba(220,53,69,0.5)', text: '#dc3545' };
            default: return { bg: 'rgba(108,117,125,0.1)', border: 'rgba(108,117,125,0.4)', text: '#6c757d' };
        }
    };

    if (isLoading) return <Container className="mt-5 text-center"><h5>Đang tải thông tin tài sản...</h5></Container>;
    if (isError) return <Container className="mt-5 text-center text-danger"><h5>Có lỗi xảy ra: {error.message}</h5></Container>;
    if (!asset) return <Container className="mt-5 text-center"><h5>Không tìm thấy thông tin tài sản</h5></Container>;

    const renderField = (label, value) => (
        <Row className="mb-2 align-items-start">
            <Col sm={4} className="text-left" style={{ fontWeight: 600, color: '#495057' }}>{label}:</Col>
            <Col sm={8}>{value || '---'}</Col>
        </Row>
    );

    return (
        <Container className="mt-4 mb-4">
            <Card className="shadow">
                <Card.Header className="bg-primary text-white text-center">
                    <h4 className="mb-0">CHI TIẾT TÀI SẢN</h4>
                </Card.Header>
                <Card.Body>
                    <Tabs>
                        <TabList>
                            <Tab>Thông tin chung</Tab>
                            <Tab>Hình ảnh & Tài liệu</Tab>
                            <Tab>Danh sách phụ kiện</Tab>
                        </TabList>

                        <TabPanel>
                            <div className="mt-4">
                                <h6 className="text-primary border-bottom pb-2 mb-3">Thông tin định danh</h6>
                                <Row>
                                    <Col md={6}>
                                        {renderField('Mã tài sản', asset.assetCode)}
                                        {renderField('Tên tài sản', asset.assetName)}
                                        {renderField('Số thẻ tài sản', asset.cardNumber)}
                                        {renderField('Ngày tạo thẻ', asset.cardCreatedDate ? moment(asset.cardCreatedDate).format('DD/MM/YYYY') : '')}
                                    </Col>
                                    <Col md={6}>
                                        {renderField('Nhóm tài sản', asset.assetGroupName)}
                                        {renderField('Phòng ban', asset.departmentName)}
                                        {renderField('Trạng thái', asset.assetStatusName)}
                                        {renderField('Thanh lý', asset.isDisposed ? 'Đã thanh lý' : 'Chưa thanh lý')}
                                    </Col>
                                </Row>

                                <h6 className="text-primary border-bottom pb-2 mt-4 mb-3">Thông số kỹ thuật</h6>
                                <Row>
                                    <Col md={6}>
                                        {renderField('Số Serial', asset.serialNumber)}
                                        {renderField('Model / Ký hiệu', asset.model)}
                                        {renderField('Nhà sản xuất', asset.manufacturer)}
                                    </Col>
                                    <Col md={6}>
                                        {renderField('Xuất xứ', asset.country)}
                                        {renderField('Công suất', asset.powerSpec)}
                                    </Col>
                                </Row>

                                <h6 className="text-primary border-bottom pb-2 mt-4 mb-3">Thông tin mua sắm</h6>
                                <Row>
                                    <Col md={6}>
                                        {renderField('Ngày mua', asset.purchaseDate ? moment(asset.purchaseDate).format('DD/MM/YYYY') : '')}
                                    </Col>
                                    <Col md={6}>
                                        {renderField('Giá trị mua', asset.purchaseValue ? asset.purchaseValue.toLocaleString('vi-VN') + ' VND' : '')}
                                    </Col>
                                </Row>
                            </div>
                        </TabPanel>

                        <TabPanel>
                            <div className="mt-4">
                                <h6 className="text-primary border-bottom pb-2 mb-3">Ảnh tài sản</h6>
                                <div className="text-center mb-4">
                                    {asset.imagePath ? (
                                        <img 
                                            src={isValidUrl(asset.imagePath) ? asset.imagePath : BaseGatewayImage + asset.imagePath} 
                                            alt={asset.assetName} 
                                            className="img-fluid rounded shadow-sm" 
                                            style={{ maxHeight: '400px' }} 
                                        />
                                    ) : (
                                        <div className="p-5 bg-light border rounded">Không có ảnh tài sản</div>
                                    )}
                                </div>

                                <h6 className="text-primary border-bottom pb-2 mt-4 mb-3">Tài liệu kèm theo</h6>
                                <ul className="list-group list-group-flush">
                                    {asset.documentLink && asset.documentLink.length > 0 ? (
                                        asset.documentLink.map((link, index) => (
                                            <li key={index} className="list-group-item d-flex justify-content-between align-items-center">
                                                <span>Tài liệu {index + 1}</span>
                                                <a href={isValidUrl(link) ? link : BaseGatewayImage + (link.startsWith('/') ? '' : '/') + link} download target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-success shadow-none">
                                                    <i className="fas fa-download mr-1" /> Tải xuống
                                                </a>
                                            </li>
                                        ))
                                    ) : (
                                        <li className="list-group-item text-muted">Không có tài liệu đính kèm</li>
                                    )}
                                </ul>
                            </div>
                        </TabPanel>

                        <TabPanel>
                            <div className="mt-4">
                                <h6 className="text-primary border-bottom pb-2 mb-3">Danh sách phụ kiện</h6>
                                <div className="table-responsive">
                                    <Table striped bordered hover size="sm">
                                        <thead>
                                            <tr className="text-dark fw-bold text-uppercase" style={{ fontSize: '12px', background: '#f8f9fa' }}>
                                                <th className="text-center py-2" style={{ width: '50px' }}>STT</th>
                                                <th className="py-2">Mã phụ kiện</th>
                                                <th className="py-2">Tên phụ kiện</th>
                                                <th className="py-2">Quy cách</th>
                                                <th className="py-2">Đơn vị</th>
                                                <th className="text-center py-2">Số lượng</th>
                                                <th className="text-center py-2">Trạng thái</th>
                                                <th className="py-2">Ghi chú</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(!asset.accessories || asset.accessories.length === 0) && (
                                                <tr>
                                                    <td colSpan="8" className="text-center text-muted">
                                                        Không có phụ kiện kèm theo
                                                    </td>
                                                </tr>
                                            )}
                                            {asset.accessories && asset.accessories.map((item, index) => (
                                                <tr key={item.id}>
                                                    <td className="text-center">{index + 1}</td>
                                                    <td>{item.materialCode}</td>
                                                    <td>{item.materialName}</td>
                                                    <td>{item.specification}</td>
                                                    <td>{item.unit}</td>
                                                    <td className="text-center">{item.quantity}</td>
                                                    <td className="text-center">
                                                        {(() => {
                                                            const s = getAccessoryStatusStyle(item.accessoryStatus);
                                                            return (
                                                                <span style={{
                                                                    display: 'inline-block',
                                                                    padding: '3px 10px',
                                                                    borderRadius: '999px',
                                                                    fontSize: '11px',
                                                                    fontWeight: 700,
                                                                    background: s.bg,
                                                                    color: s.text,
                                                                    border: `1px solid ${s.border}`,
                                                                    whiteSpace: 'nowrap',
                                                                }}>
                                                                    {item.accessoryStatus}
                                                                </span>
                                                            );
                                                        })()}
                                                    </td>
                                                    <td>{item.note}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </div>
                        </TabPanel>
                    </Tabs>
                </Card.Body>
                <Card.Footer className="text-center text-muted small">
                    Hệ thống Quản lý Trang thiết bị - {new Date().getFullYear()}
                </Card.Footer>
            </Card>
        </Container>
    );
};

export default AssetPublicDetail;
